import { useCallback, useRef, useState } from "react";
import { Download, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { brandApi, type BrandSlot } from "../../../api/brand";
import { PageHeader } from "../../../components/ComingSoon";
import { Button } from "../../../components/ui";
import { useBrandStatus } from "../../../components/BrandStatus";
import { BrandLogo } from "../../../components/Chrome";
import { LoadState, useLoad } from "../shared";

const kb = (n: number) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);

function SlotCard({ s, onDone }: { s: BrandSlot; onDone: () => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const { refresh } = useBrandStatus();
  const pick = async (file?: File) => {
    if (!file) return;
    if (file.size > s.max_mb * 1048576) { toast.error(`File is larger than ${s.max_mb} MB.`); return; }
    setBusy(true);
    try { const r = await brandApi.upload(s.slot, file); toast.success(`${s.label} uploaded (version ${r.version})`); refresh(); onDone(); }
    catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  const c = s.current;
  return (
    <section className="glass rounded-[20px] p-6" data-testid={`brand-slot-${s.slot}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-medium text-white">{s.label}</h2>
          {s.help && <p className="mt-1 text-sm text-zinc-300" data-testid={`brand-help-${s.slot}`}>{s.help}</p>}
          {s.slot === "logo" && <p className="mt-1 text-xs text-signal" data-testid="brand-logo-tip">Best: PNG with a transparent background.</p>}
          <p className="mt-1 text-xs text-zinc-400">{s.accepted.map((t) => t.split("/")[1].toUpperCase().replace("JPEG", "JPG")).join(", ")} · up to {s.max_mb} MB · larger than {s.max_dim}px is scaled down · photo metadata (EXIF) is removed</p>
        </div>
        <input ref={input} type="file" accept={s.accepted.join(",")} className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid={`brand-file-input-${s.slot}`} />
        <Button onClick={() => input.current?.click()} loading={busy} data-testid={`brand-upload-btn-${s.slot}`}>
          <UploadCloud className="mr-2 h-4 w-4" />{c ? "Upload new version" : "Upload file"}
        </Button>
      </div>
      {c ? (
        <div className="mt-6 grid gap-6 md:grid-cols-[minmax(0,320px)_1fr]">
          <div className="flex items-center justify-center overflow-hidden rounded-xl border border-white/10 bg-[repeating-conic-gradient(#1c1f26_0%_25%,#14161b_0%_50%)] bg-[length:20px_20px] p-3">
            <img src={brandApi.fileUrl(s.slot, c.version)} alt={`Current ${s.label}`} className="max-h-64 w-auto object-contain" data-testid={`brand-preview-${s.slot}`} />
          </div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm" data-testid={`brand-meta-${s.slot}`}>
            <dt className="text-zinc-500">File</dt><dd className="text-white">{c.filename}</dd>
            <dt className="text-zinc-500">Version</dt><dd className="text-white" data-testid={`brand-version-${s.slot}`}>v{c.version}</dd>
            <dt className="text-zinc-500">Size</dt><dd className="text-white">{c.width} × {c.height}px · {kb(c.size)}{c.resized ? " (scaled down)" : ""}</dd>
            <dt className="text-zinc-500">Uploaded</dt><dd className="text-white">{new Date(c.uploaded_at).toLocaleString()}</dd>
            <dd className="col-span-2 mt-3">
              <a href={brandApi.fileUrl(s.slot, c.version, true)} className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.12em] text-signal hover:text-white" data-testid={`brand-download-${s.slot}`}>
                <Download className="mr-1.5 h-4 w-4" />Download
              </a>
            </dd>
            {s.slot === "logo" && (
              <dd className="col-span-2 mt-4" data-testid="brand-logo-live-preview">
                <p className="label-caps mb-2 text-zinc-500">As shown on sign-in</p>
                <div className="inline-flex rounded-xl border border-white/10 bg-ink/60 p-4"><BrandLogo size="sm" /></div>
              </dd>
            )}
          </dl>
        </div>
      ) : <p className="mt-6 text-sm text-zinc-400" data-testid={`brand-empty-${s.slot}`}>No file uploaded yet.</p>}
      {s.versions.length > 1 && (
        <details className="mt-6 text-sm text-zinc-400" data-testid={`brand-history-${s.slot}`}>
          <summary className="cursor-pointer text-zinc-300">Previous versions ({s.versions.length - 1})</summary>
          <ul className="mt-2 space-y-1">
            {s.versions.filter((v) => !v.is_current).map((v) => (
              <li key={v.id}>v{v.version} · {v.filename} · {v.width}×{v.height} · {new Date(v.uploaded_at).toLocaleDateString()} · <a className="text-signal hover:text-white" href={brandApi.fileUrl(s.slot, v.version, true)}>Download</a></li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

export default function BrandingFiles() {
  const [nonce, setNonce] = useState(0);
  const loader = useCallback(() => brandApi.list(), [nonce]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useLoad(loader);
  return (
    <div data-testid="admin-branding-page" className="animate-rise space-y-6">
      <PageHeader title="Branding files" />
      <p className="max-w-2xl text-sm text-zinc-400">Upload the source files for the app logo and the crew sign-in background. Uploading here does not change the live site yet; the files are processed and applied separately. Every upload is kept as a new version.</p>
      <LoadState error={error} loading={!data && !error} testId="branding-load-error" />
      {data?.slots.map((s) => <SlotCard key={s.slot} s={s} onDone={() => setNonce((n) => n + 1)} />)}
    </div>
  );
}
